/* eslint-disable react/no-unknown-property */
import { PaginationType } from "@/libs/types";
import clsx from "clsx";
import Link from "next/link";
import { usePathname, useSearchParams } from "next/navigation";
import React from "react";
import Next from "./Next";
import Noref from "./Noref";
import Previous from "./Previous";

type PageNumberTypes = {
    currentPage: number
    pageSize: number
    total: number
    pageNumbersToShow: number
}

interface PaginationProps {
    pagination: PaginationType
    locale: string
}


const Pagination: React.FunctionComponent<PaginationProps> = ({
    pagination,
    locale,
}) => {

    const pathname = usePathname()
    const searchParams = useSearchParams()

    const rebuildPageNumbers = ({ currentPage, pageSize, total, pageNumbersToShow }: PageNumberTypes) => {
        const lastPageNumber = Math.ceil(total / pageSize)
        const currentPageNumber =
            currentPage <= lastPageNumber ? currentPage : lastPageNumber
        const maxPagesBeforeCurrentPage = Math.floor(pageNumbersToShow / 2)
        const maxPagesAfterCurrentPage = Math.ceil(pageNumbersToShow / 2) - 1
        let startPage = 1
        let endPage = lastPageNumber

        if (lastPageNumber <= 1) {
            return [] // Don't show numbers if there's only 1 page
        }

        if (currentPageNumber <= maxPagesBeforeCurrentPage) {
            // near the start
            startPage = 1
            endPage = pageNumbersToShow
        } else if (currentPageNumber + maxPagesAfterCurrentPage >= lastPageNumber) {
            // near the end
            startPage = lastPageNumber - pageNumbersToShow + 1
        } else {
            // somewhere in the middle
            startPage = currentPageNumber - maxPagesBeforeCurrentPage
            endPage = currentPageNumber + maxPagesAfterCurrentPage
        }

        let pageNumbers: (string | number)[] = Array.from(Array(endPage + 1 - startPage).keys())
            .map((pageNumber) => startPage + pageNumber)
            .filter((pageNumber) => pageNumber <= lastPageNumber && pageNumber > 0)

        try {
            let first = pageNumbers[0] as number
            if (first > 1) {
                if (first <= 2) {
                    pageNumbers = [1, ...pageNumbers]
                } else {
                    const ellipsis = first > 3 ? '...' : 2
                    pageNumbers = [1, ellipsis, ...pageNumbers]
                }
            }
        } catch (error: any) {
            // console.log(error?.message)
        }

        try {
            let end = pageNumbers[pageNumbers.length - 1] as number
            if (end !== lastPageNumber) {
                if (end === lastPageNumber - 1) {
                    pageNumbers = [...pageNumbers, lastPageNumber]
                } else {
                    const ellipsis =
                        end < lastPageNumber - 2
                            ? '...'
                            : lastPageNumber - 1
                    pageNumbers = [...pageNumbers, ellipsis, lastPageNumber]
                }
            }
        } catch (error: any) {
            // console.log(error?.message)
        }
        return pageNumbers
    }

    if (pagination.pageCount === 1) return null;

    const pages = rebuildPageNumbers({
        currentPage: pagination.page,
        pageSize: pagination.pageSize,
        total: pagination.total,
        pageNumbersToShow: 3
    })
    const isLastPage = pagination.page >= pagination.pageCount

    const getHref = (next: number) => {
        const search = searchParams.get('search')
        if (!!search) {
            return `${pathname}?search=${search}&page=${next}`
        } else {
            return `${pathname}?page=${next}`
        }
    }

    return (
        <div>
            <ul className='pagination'>
                {(pagination.page > 1) && <Previous locale={locale} href={getHref(pagination.page - 1)} />}
                <div className='pagination-main'>
                    {pages.map((page: number | string) => {
                        if (page === "...") return <Noref key={page} />
                        return (
                            <li key={page} className={clsx("item", page === pagination.page ? "active" : "")}>
                                <Link href={getHref(page as number)} passHref>
                                    {page}
                                </Link>
                            </li>
                        )
                    })}
                </div>
                {!isLastPage && <Next locale={locale} href={getHref(pagination.page + 1)} />}
            </ul>
        </div>
    );
};

export default Pagination;
